import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  Avatar,
  useTheme,
  useMediaQuery,
  CircularProgress,
  DialogTitle,
  DialogActions,
  Popover,
} from "@mui/material";

import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";

import EmojiPicker from "emoji-picker-react";

import messages_bg from "../../../assets/img/messages_bg.png";
import { sweetRed } from "../../../theme/Colors";

import ChatBubble from "./ChatBubble";
import { useAuth } from "../../../context/AuthContext";
import { useChatContext } from "../../../context/ChatContext";
import { useWebSocket } from "../../../context/WebSocketContext";
import { useMutualLikes } from "../../../context/MutualLikesContext";
import axiosInstance from "../../../api_calls/axiosInstance";
import { showSuccess } from "../../../utils/toastUtils";
import Configs from "../../../utils/Configs";
import { v4 as uuidv4 } from "uuid";

function GlobalChatModal() {
  const { selectedContact, isOpen, closeChat } = useChatContext();
  const { sendMessage, registerHandler, dequeueMessages } = useWebSocket();
  const { user } = useAuth();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const contact = selectedContact;

  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);

  // 🔥 Emoji
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const inputRef = useRef(null);

  const messagesEndRef = useRef(null);
  const isDeletingRef = useRef(false);
  const firstLoadRef = useRef(false);
  const allMessageIdsRef = useRef(new Set());

  /* -----------------------------
        EMOJI HANDLERS
  ------------------------------*/
  const handleEmojiOpen = (e) => {
    setEmojiAnchor(e.currentTarget);
  };

  const handleEmojiClose = () => {
    setEmojiAnchor(null);
  };

  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    const input = inputRef.current;

    if (!input) {
      setMessage((prev) => prev + emoji);
      return;
    }

    const start = input.selectionStart;
    const end = input.selectionEnd;

    const newText =
      message.substring(0, start) + emoji + message.substring(end);

    setMessage(newText);

    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(start + emoji.length, start + emoji.length);
    });
  };

  /* -----------------------------
        MESSAGE MERGER
  ------------------------------*/
  const addUniqueMessages = (newMessages, options = { prepend: false }) => {
    setMessages((prev) => {
      const itemsToAdd = [];
      const prevCopy = [...prev];

      for (const m of newMessages) {
        const mIdStr = m.id ? String(m.id) : null;
        const mTempStr = m.tempId ? String(m.tempId) : null;

        if (
          (mIdStr && allMessageIdsRef.current.has(mIdStr)) ||
          (mTempStr && allMessageIdsRef.current.has(mTempStr))
        )
          continue;

        itemsToAdd.push(m);

        if (mIdStr) allMessageIdsRef.current.add(mIdStr);
        if (mTempStr) allMessageIdsRef.current.add(mTempStr);
      }

      const combined = options.prepend
        ? [...itemsToAdd, ...prevCopy]
        : [...prevCopy, ...itemsToAdd];

      return combined.sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
    });
  };

  /* -----------------------------
        REST HISTORY
  ------------------------------*/
  const fetchMessages = async () => {
    if (!contact?.id || !user?.user_id) return;

    setLoadingMessages(true);
    try {
      const response = await axiosInstance.get(Configs.apiGetMessagesEp, {
        params: { recipient_user_id: contact.id, page_number: 1 },
      });

      if (response.data.status === "success" && response.data.data) {
        const formatted = response.data.data.map((msg) => ({
          id: msg.id,
          text: msg.body,
          fromMe: msg.sender_id === user.user_id,
          timestamp: msg.sent_at,
          status: msg.status,
        }));

        addUniqueMessages(formatted, { prepend: true });
        firstLoadRef.current = true;
        setInitialFetchDone(true);
        dequeueMessages("chat");
      }
    } catch (e) {
      console.error("Failed to fetch messages:", e);
    } finally {
      setLoadingMessages(false);
    }
  };

  /* -----------------------------
        OPEN / RESET
  ------------------------------*/
  useEffect(() => {
    if (isOpen) {
      allMessageIdsRef.current.clear();
      firstLoadRef.current = false;
      setMessages([]);
      setInitialFetchDone(false);
      fetchMessages();
    }
  }, [isOpen, contact?.id]);

  useEffect(() => {
    if (messagesEndRef.current && initialFetchDone && !isDeletingRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, initialFetchDone]);

  /* -----------------------------
        WEBSOCKET (LIVE ONLY)
  ------------------------------*/
  useEffect(() => {
    if (!isOpen || !contact?.id) return;

    const handleIncomingChat = (data) => {
      if (
        !firstLoadRef.current ||
        data.tag !== "chat" ||
        String(data.payload.sender_id) !== String(contact.id)
      )
        return;

      addUniqueMessages([
        {
          id: data.payload.message_id ?? data.payload.id,
          text: data.payload.message,
          fromMe: false,
          timestamp: data.payload.timestamp,
          status: "delivered",
        },
      ]);
    };

    registerHandler("chat", handleIncomingChat);
    return () => registerHandler("chat", null);
  }, [isOpen, contact?.id]);

  /* -----------------------------
        SEND
  ------------------------------*/
  const handleSendMessage = () => {
    if (!message.trim() || !contact?.id) return;

    const tempId = uuidv4();

    sendMessage({
      tag: "chat",
      payload: { recipient: contact.id, message, tempId },
    });

    addUniqueMessages([
      {
        tempId,
        text: message,
        fromMe: true,
        timestamp: new Date().toISOString(),
        status: "sending",
      },
    ]);

    setMessage("");
  };

  if (!isOpen || !contact) return null;

  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={closeChat}
      PaperProps={{
        sx: {
          width: isSmallScreen ? "100%" : "33%",
          height: "100%",
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
        },
      }}
    >
      <AppBar position="sticky" sx={{ backgroundColor: sweetRed.light }}>
        <Toolbar>
          <Box display="flex" alignItems="center" flex={1}>
            <Avatar src={contact.avatar} sx={{ mr: 1 }} />
            <Typography
              sx={{ fontSize: "1.1rem", textTransform: "capitalize" }}
            >
              {contact.name.toLowerCase()}
            </Typography>
          </Box>
          <IconButton edge="end" color="inherit" onClick={closeChat}>
            <HighlightOffIcon fontSize="large" />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        flex={1}
        overflow="auto"
        p={2}
        sx={{
          paddingBottom: 10,
          backgroundImage: `url(${messages_bg})`,
          backgroundSize: "cover",
        }}
      >
        {loadingMessages && (
          <Box display="flex" justifyContent="center" my={1}>
            <CircularProgress size={24} />
          </Box>
        )}

        {messages.map((msg) => (
          <ChatBubble
            key={msg.id ? `srv-${msg.id}` : `tmp-${msg.tempId}`}
            text={msg.text}
            fromMe={msg.fromMe}
            timestamp={msg.timestamp}
            status={msg.status}
          />
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* INPUT BAR */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        p={2}
        bgcolor="#fff"
        display="flex"
        gap={1}
        alignItems="flex-end"
        borderTop="1px solid #ddd"
      >
        <IconButton onClick={handleEmojiOpen}>
          <InsertEmoticonIcon />
        </IconButton>

        <TextField
          inputRef={inputRef}
          placeholder="Type a message"
          fullWidth
          size="small"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) =>
            e.key === "Enter" &&
            !e.shiftKey &&
            (e.preventDefault(), handleSendMessage())
          }
          multiline
          maxRows={5}
        />

        <Button
          variant="contained"
          sx={{ backgroundColor: sweetRed.light }}
          onClick={handleSendMessage}
        >
          Send
        </Button>
      </Box>

      {/* EMOJI PICKER */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={handleEmojiClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <EmojiPicker onEmojiClick={handleEmojiClick} height={350} width={300} />
      </Popover>
    </Dialog>
  );
}

export default GlobalChatModal;
